import classNames from 'classnames';
import { useId, type ForwardedRef, forwardRef } from 'react';

import type { PrimarySubNavigation, PrimarySubNavigationSection } from 'src/types/components';

import Text from 'components/BaseHelpers/Text';
import TextCTA from 'components/BaseHelpers/TextCTA';

import styles from './PrimarySubNavigation.module.scss';

type NavSectionProps = {
  section: PrimarySubNavigationSection;
  primaryLinkId: string;
  onClick: () => void;
};
const NavSection = ({
  section: { groupLabel, links },
  primaryLinkId,
  onClick,
}: NavSectionProps) => {
  const groupId = useId();
  return (
    <>
      {!!groupLabel && (
        <p id={`${groupId}-label`} className={styles.groupLabel}>
          <Text field={groupLabel} />
        </p>
      )}
      <ul
        className={styles.linkList}
        id={groupId}
        aria-labelledby={`${groupLabel ? groupId : primaryLinkId}-label`}
      >
        {links.map((link, index) => {
          return (
            <li key={index} className={styles.sectionLink}>
              <TextCTA {...link} tag="a" iconVariant="yellow" onClick={onClick} />
            </li>
          );
        })}
      </ul>
    </>
  );
};

export type Anchor = 'left' | 'center' | 'right';

type OwnProps = {
  className?: string;
  anchor?: Anchor;
  ref?: ForwardedRef<HTMLDivElement>;
  onClick: () => void;
};
export type PrimarySubNavigationProps = OwnProps & PrimarySubNavigation;

const PrimarySubNavigation = forwardRef<HTMLDivElement, PrimarySubNavigationProps>(
  function PrimarySubNavigationComponent(props: PrimarySubNavigationProps, ref): JSX.Element {
    const primaryLinkId = useId();
    const { primaryLink, navigationSections, className, anchor = 'right', onClick } = props;
    const maxRowCount = navigationSections.reduce((maxCount, section) => {
      return Math.max(section.links.length, maxCount);
    }, 0);
    return (
      <div
        ref={ref}
        className={classNames(
          styles.subNavigation,
          styles[`subNavigation--anchor-${anchor}`],
          className
        )}
        style={{
          '--PrimarySubNavigation__column-count': navigationSections.length,
          '--PrimarySubNavigation__max-row-count': maxRowCount,
        }}
      >
        <TextCTA
          size="large"
          iconVariant="yellow"
          tag="a"
          {...primaryLink}
          className={styles.primaryLink}
          id={`${primaryLinkId}-label`}
          onClick={onClick}
        />
        <ul className={styles.sectionsWrapper} aria-labelledby={`${primaryLinkId}-label`}>
          {navigationSections.map((section, index) => {
            return (
              <li key={index} className={styles.section}>
                <NavSection onClick={onClick} section={section} primaryLinkId={primaryLinkId} />
              </li>
            );
          })}
        </ul>
      </div>
    );
  }
);

export default PrimarySubNavigation;
